import * as React from 'react'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export interface ComboboxOption {
  label: string | React.ReactNode
  value: string
}

interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string) => void
  onSearch?: (search: string, signal: AbortSignal) => void
  placeholder?: string
  emptyText?: string
  searchPlaceholder?: string
  isLoading?: boolean
  disabled?: boolean
  className?: string
  isInvalid?: boolean
  renderOption?: (option: ComboboxOption) => React.ReactNode
  minSearchLength?: number
  shouldFilter?: boolean
}

export function Combobox({
  options,
  value,
  onValueChange,
  onSearch,
  placeholder = 'Select option...',
  emptyText = 'No results found.',
  searchPlaceholder = 'Search...',
  isLoading = false,
  disabled = false,
  className,
  isInvalid = false,
  renderOption,
  minSearchLength: _minSearchLength = 0,
  shouldFilter,
}: ComboboxProps) {
  const effectiveShouldFilter = shouldFilter ?? !onSearch
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState('')
  const [hasSearched, setHasSearched] = React.useState(false)
  const abortControllerRef = React.useRef<AbortController>()

  const selectedOption = React.useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  )

  const runSearch = (search: string) => {
    if (!onSearch) return
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller
    setHasSearched(true)
    onSearch(search, controller.signal)
  }

  const handleSearchChange = (search: string) => {
    setSearchValue(search)
  }

  React.useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const showEmpty = hasSearched && !isLoading && options.length === 0

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="tertiary"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full rounded-[16px] justify-between !bg-monochrome-3-60 !text-monochrome-1 hover:!bg-monochrome-3-60/90 border !border-monochrome-120',
            isInvalid && '!border-accent-3',
            !selectedOption && 'text-monochrome-120',
            className,
          )}
        >
          <span className="truncate">
            {selectedOption?.label || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-4 bg-monochrome-3-60-solid border-monochrome-120 rounded-[16px]"
        align="center"
      >
        <Command
          className="bg-monochrome-3-60-solid"
          shouldFilter={effectiveShouldFilter}
        >
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              if (onSearch) {
                e.preventDefault()
                e.stopPropagation()
                runSearch(searchValue)
              }
            }}
            className="bg-monochrome-4 !text-monochrome-1"
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-monochrome-120" />
                <span className="ml-2 text-sm text-monochrome-120">
                  Loading...
                </span>
              </div>
            ) : showEmpty ? (
              <div className="py-6 text-center text-sm text-monochrome-120">
                {emptyText}
              </div>
            ) : (
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={(currentValue) => {
                      onValueChange?.(
                        currentValue === value ? '' : currentValue,
                      )
                      setOpen(false)
                      setSearchValue('')
                    }}
                    className="text-monochrome-1 hover:bg-accent-1/10"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {renderOption ? renderOption(option) : option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
